import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { errorText } from '../health/healthConfig';

export const OTCOrderCard = ({ order, api, refresh, staff = false }) => {
  const [busy, setBusy] = useState(false);
  const [refundId, setRefundId] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [collectOpen, setCollectOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const act = async (action, extra = {}) => {
    setBusy(true); try {
      await api.post(staff ? `/staff/portal/otc/orders/${order.order_id}/decision` : `/otc/orders/${order.order_id}/${action}`, staff ? { action, ...extra } : {});
      await refresh(); toast.success('Order updated');
      return true;
    } catch (e) { toast.error(errorText(e)); return false; } finally { setBusy(false); }
  };
  const setPriority = async (urgent, reason = '') => {
    setBusy(true); try {
      await api.post(`/care/requests/${order.order_id}/priority`, { urgent, reason });
      await refresh(); toast.success(urgent ? 'Moved up the queue' : 'Urgent flag cleared');
      setFlagOpen(false); setFlagReason('');
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const id = order.order_id;
  const status = order.status.replaceAll('_', ' ');
  const opening = order.opening_hours;
  const isPaidLike = ['paid', 'awaiting_refund'].includes(order.payment_status);
  const requestingReview = isPaidLike && !staff;
  const staffPaidCancel = isPaidLike && staff;
  const canTriage = staff && !['collected', 'cancelled'].includes(order.status);
  const urgent = order.priority === 'urgent';
  return <article className="otc-order" data-testid={`otc-order-${id}`}>
    <div className="flex justify-between gap-3 items-start"><div className="min-w-0"><h3 className="font-semibold break-words" data-testid={`otc-branch-${id}`}>{order.branch_name}</h3><p className="text-xs break-all text-slate-500" data-testid={`otc-reference-${id}`}>{order.reference}</p></div><strong className="whitespace-nowrap" data-testid={`otc-total-${id}`}>{order.currency} {(order.total_minor / 100).toFixed(2)}</strong></div>
    <div className="flex gap-2 flex-wrap my-3"><span className="otc-status" data-testid={`otc-status-${id}`}>Collection: {status}</span><span className="otc-status" data-testid={`otc-payment-${id}`}>Payment: {order.payment_status.replaceAll('_', ' ')}</span>{urgent && <span className="otc-status otc-status-urgent" data-testid={`otc-priority-${id}`}>{staff ? 'Urgent' : 'Being prioritised'}</span>}</div>
    <ul className="text-sm space-y-1" data-testid={`otc-items-${id}`}>{order.items.map(item => <li key={item.product_id}>{item.quantity} × {item.name}</li>)}</ul>
    {order.status === 'requested' && <p className="text-sm text-slate-600 mt-3" data-testid={`otc-request-note-${id}`}>Awaiting pharmacy acceptance. Not confirmed or paid.</p>}
    {order.resolution && <p role="status" className="portal-notice mt-3" data-testid={`otc-resolution-${id}`}>{order.resolution}</p>}
    {order.hold_expires_at && order.payment_status === 'unpaid' && <p className="text-xs mt-3" data-testid={`otc-hold-${id}`}>Online stock hold ends {new Date(order.hold_expires_at).toLocaleString()}.</p>}
    <details className="text-sm my-3" data-testid={`otc-hours-${id}`}><summary className="cursor-pointer py-2" data-testid={`otc-hours-toggle-${id}`}>Collection opening hours</summary>{opening?.weekly ? <div><p>{opening.timezone}</p>{Object.entries(opening.weekly).map(([day, hours]) => <p className="capitalize" key={day}>{day}: {hours.closed ? 'Closed' : `${hours.opens}–${hours.closes}`}</p>)}</div> : <p>Contact the branch to confirm collection hours.</p>}</details>
    <div className="flex flex-wrap gap-2">
      {!staff && order.authorization_url && order.payment_status === 'unpaid' && order.status !== 'cancelled' && <Button asChild className="portal-primary" data-testid={`otc-pay-${id}`}><a href={order.authorization_url}>Continue online payment</a></Button>}
      {order.payment_choice === 'online' && !['refunded'].includes(order.payment_status) && <Button variant="outline" disabled={busy} onClick={() => act(staff ? 'verify-payment' : 'verify')} data-testid={`otc-verify-${id}`}>Check payment</Button>}
      {staff && order.status === 'requested' && <Button disabled={busy} onClick={() => act('confirm')} data-testid={`otc-confirm-${id}`}>Confirm availability</Button>}
      {staff && order.status === 'confirmed' && <Button disabled={busy} onClick={() => act('ready')} data-testid={`otc-ready-${id}`}>Ready for collection</Button>}
      {staff && ['ready', 'collecting'].includes(order.status) && <Button disabled={busy} onClick={() => setCollectOpen(true)} data-testid={`otc-collect-${id}`}>Record collection</Button>}
      {(!['collected', 'collecting', 'cancelled'].includes(order.status) || (staff && order.status === 'cancelled' && order.payment_status === 'paid')) && <Button variant="outline" disabled={busy || (!staff && order.cancellation_requested)} onClick={() => setCancelOpen(true)} data-testid={`otc-cancel-${id}`}>{requestingReview ? 'Request cancellation' : 'Cancel order'}</Button>}
      {canTriage && (urgent
        ? <Button variant="ghost" disabled={busy} onClick={() => setPriority(false)} data-testid={`otc-clear-urgent-${id}`}><ArrowDown size={14} className="mr-1.5" />Clear urgent</Button>
        : <Button variant="outline" disabled={busy} onClick={() => { setFlagReason(''); setFlagOpen(true); }} data-testid={`otc-flag-urgent-${id}`}><ArrowUp size={14} className="mr-1.5" />Flag urgent</Button>)}
      {staff && !['collected', 'cancelled'].includes(order.status) && <Button variant="outline" asChild><Link to={'/pos?portal_otc=' + id} data-testid={`otc-pos-${id}`}>Open in POS</Link></Button>}
    </div>
    {staff && order.payment_status === 'awaiting_refund' && <div className="flex gap-2 mt-3"><input className="portal-native-select min-w-0" aria-label="Paystack refund ID" placeholder="Paystack refund ID" value={refundId} onChange={e => setRefundId(e.target.value)} data-testid={`otc-refund-id-${id}`} /><Button disabled={busy || !refundId} onClick={() => act('verify-refund', { refund_id: refundId })} data-testid={`otc-refund-verify-${id}`}>Verify refund</Button></div>}

    <AlertDialog open={cancelOpen} onOpenChange={(open) => !busy && setCancelOpen(open)}>
      <AlertDialogContent className="max-w-[calc(100%-2rem)] rounded-xl sm:max-w-lg" data-testid={`otc-cancel-confirm-dialog-${id}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>{requestingReview ? 'Request cancellation review?' : 'Cancel this order?'}</AlertDialogTitle>
          <AlertDialogDescription>
            {requestingReview
              ? 'Your pharmacy will review your request. A refund is not automatic.'
              : staffPaidCancel
                ? "This order was paid — cancelling it will need a refund to be completed and verified afterwards."
                : "This unpaid order will be cancelled and can't be collected afterwards."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid={`otc-cancel-confirm-dismiss-${id}`}>Keep order</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); act('cancel').then((ok) => { if (ok) setCancelOpen(false); }); }}
            disabled={busy}
            data-testid={`otc-cancel-confirm-${id}`}
          >
            {requestingReview ? 'Request cancellation' : 'Cancel order'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog open={collectOpen} onOpenChange={(open) => !busy && setCollectOpen(open)}>
      <AlertDialogContent className="max-w-[calc(100%-2rem)] rounded-xl sm:max-w-lg" data-testid={`otc-collect-confirm-dialog-${id}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>{order.payment_status === 'paid' ? 'Confirm handover to patient?' : 'Confirm payment received?'}</AlertDialogTitle>
          <AlertDialogDescription>
            {order.payment_status === 'paid'
              ? 'This marks the order as handed to the patient. Already paid — no payment will be collected now.'
              : 'This marks the order as handed over and recorded as paid in cash/in person.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid={`otc-collect-confirm-dismiss-${id}`}>Not yet</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); act('collect', { payment_received: true }).then((ok) => { if (ok) setCollectOpen(false); }); }}
            disabled={busy}
            data-testid={`otc-collect-confirm-${id}`}
          >
            Record collection
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <Dialog open={flagOpen} onOpenChange={(open) => !busy && setFlagOpen(open)}>
      <DialogContent className="max-w-[calc(100%-2rem)] rounded-xl sm:max-w-lg" data-testid={`otc-flag-dialog-${id}`} closeProps={{ 'data-testid': `otc-flag-dialog-close-${id}` }}>
        <DialogHeader>
          <DialogTitle>Move this order up the queue</DialogTitle>
          <DialogDescription>The patient sees only that they are being prioritised. Your reason stays internal.</DialogDescription>
        </DialogHeader>
        <label className="care-label">
          Reason (optional)
          <Textarea
            value={flagReason}
            onChange={(e) => setFlagReason(e.target.value)}
            maxLength={300}
            placeholder="e.g. waiting at the counter, antibiotic course due today"
            data-testid={`otc-flag-reason-${id}`}
          />
        </label>
        <Button disabled={busy} onClick={() => setPriority(true, flagReason)} data-testid={`otc-flag-submit-${id}`}>
          {busy ? 'Flagging…' : 'Flag urgent'}
        </Button>
      </DialogContent>
    </Dialog>
  </article>;
};